import { html, LitElement, nothing } from 'lit';
import '@cof/fs-typography-wc/fs-heading.js';
import '@cof/fs-typography-wc/fs-body-text.js';
import '@cof/fs-button-wc/fs-button.js';
import '@cof/fs-divider-wc/fs-divider.js';
import '@cof/fs-text-field-wc/fs-text-field.js';
import '@cof/fs-select-wc/fs-select.js';
import { TagKey } from '@cof/tag-catalog-fs-esign-mfe';
import '../shared/accordion/esign-accordion.js';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit';
import { HighlineService } from '@cof/auto-refinance-node-highline';
import {
  displayProgressBarEvent,
  updateMacroStepEvent,
  updateMicroStepEvent
} from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import {
  DEFAULT_STATE_SELECT,
  getAdditionalInputsForState,
  getEtitleHeader,
  publishTagForInputChange,
  TitleType,
  MICHIGAN_BORROWER_DL_STATE,
  DC_BORROWER_DL_STATE,
  BORROWER_DL_STATE
} from './etitle.config.js';
import { DebugService } from '../shared/services/debug.service.js';
import { ETitleStyles } from './etitle.styles.js';
import { EsignService } from '../shared/services/e-sign.service.js';
import './etitle-modal/etitle-modal.js';
import './etitle-download/etitle-download.component.js';
import { AdditionalInfoRepo } from '../../store/additional-info-store/additional-info-store.js';
import { ETitleDetailsService } from '../shared/services/etitle-details/etitle-details.service.js';
import { ExperimentService } from '../shared/services/experiment.service.js';
import { FeatureToggleService } from '../shared/services/feature-toggle.service.js';
import { REFI_ESIGN_CONST } from '../../constants/app.constants.js';
import { stratum } from '../../constants/stratum.js';
import { SessionService } from '../core/services/session/session.service.js';

if (!REFI_ESIGN_CONST.isProdEnv) {
  import('@ngneat/elf-devtools').then(module => {
    const { devTools } = module;
    devTools({
      name: 'esign-etitle'
    });
  });
}

export class ETitle extends LitElement {
  static properties = {
    config: {
      type: Object,
      state: true
    },
    additionalInputs: {
      type: Array,
      state: true
    },
    disableButton: {
      type: Boolean,
      state: true
    },
    loading: {
      type: Boolean,
      state: true
    },
    experiments: {
      type: Object,
      hasChanged: (newVal, oldVal) => {
        if (newVal !== oldVal) {
          // loop through this.experiments and add the object name to experimentIds array if it's eligible
          const experimentIds = [];
          Object.keys(newVal).forEach(experiment => {
            if (newVal[experiment].isEligible) {
              experimentIds.push(newVal[experiment].allocation);
            } else {
              experimentIds.push(`${experiment}-ineligible`);
            }
          });
          ExperimentService.updateExperiments(
            experimentIds,
            this.existingExperimentIds
          );
          this.existingExperimentIds = experimentIds;
          FeatureToggleService.updateIsETitleIconicAndToggledOn();
          return true;
        }
        return false;
      }
    },
    outOfOrder: {
      type: Boolean,
      attribute: 'out-of-order'
    },
    eTitleError: {
      type: Boolean,
      state: true
    },
    isPoataUnskippable: {
      type: Boolean,
      state: true
    }
  };

  static styles = [fsTypography, ETitleStyles];

  constructor() {
    super();
    this.debugService = new DebugService(this.className);
    this.additionalInputs = [];
    this.disableButton = false;
    this.eTitleError = false;
    this.additionalInfoRepo = new AdditionalInfoRepo();
    HighlineService.urlPrefix = REFI_ESIGN_CONST.HIGHLINE.URL_PREFIX();
    this.config = {};
    this.isPoataUnskippable = false;
    const appId = SessionService.session?.currentApp?.appId || '';
    stratum.setExternalVar('applicationId', appId);
  }

  connectedCallback() {
    super.connectedCallback();
    const ev = new CustomEvent('host-app-event', {
      detail: {
        actionName: 'state-changed',
        data: {
          state: ''
        },
        forceUrlUpdate: true
      },
      composed: true,
      bubbles: true
    });
    if (this.outOfOrder) {
      displayProgressBarEvent(false);
    } else {
      updateMacroStepEvent('Verify information', ['1', '2', '3', '4', '5']);
      updateMicroStepEvent('1');
    }
    this.dispatchEvent(ev);
    this.getConfig();
    // Fire tag to show skip button is loaded. Could move this line to integrate with feature toggle later
    stratum.publishTag(
      TagKey.POA_AND_TITLE_AUTHORIZATION_SKIP_FOR_NOW_DISPLAYED
    );
  }

  async getConfig() {
    this.loading = true;
    try {
      const config = await ETitleDetailsService.getETitleDetails();
      if (config) {
        this.config = config;
        this.additionalInputs = getAdditionalInputsForState(this.config);
        this.additionalInfoRepo.setAdditionalInfo(this.additionalInputs);
        const tagMap = {
          [TitleType.PoA]: TagKey.PAGE_VIEW_COAF_REFI_ESIGN_POA_,
          [TitleType.TitleAuth]: TagKey.PAGE_VIEW_COAF_REFI_ESIGN_TITLE_AUTH_,
          [TitleType.TitleApp]: TagKey.PAGE_VIEW_COAF_REFI_ESIGN_TITLE_APP,
          [TitleType.SecurityAgreement]:
            TagKey.PAGE_VIEW_COAF_REFI_ESIGN_SECURITY_AGREEMENT
        };
        stratum.publishTag(tagMap[this.config?.documentType]);
      } else {
        const ev = new CustomEvent('show-error-page', {
          composed: true,
          bubbles: true
        });
        this.dispatchEvent(ev);
      }
    } catch (ex) {
      this.debugService.error('Unable to fetch document details', ex);
    } finally {
      this.loading = false;
    }
  }

  generateBorrowerFormFields() {
    return this.additionalInputs
      .filter(inputData => inputData.isCoBorrowerInput !== true)
      .map(inputData => this.createFormField(inputData));
  }

  generateCoBorrowerFormFields() {
    return this.additionalInputs
      .filter(inputData => inputData.isCoBorrowerInput === true)
      .map(inputData => this.createFormField(inputData));
  }

  createFormField(inputData) {
    if (inputData.inputType === 'select') {
      return html` <fs-select
        error-text="${inputData.hasError ? inputData.errorMessage : ''}"
        ?required="${!inputData.optional}"
        @change="${this.inputEvent(inputData)}"
        id="${inputData.fieldName}"
        ?disabled="${inputData.disabled}"
        helper-text="${inputData.helperText}"
        ><span slot="label">${inputData.label}</span>
        <option ?selected="${!inputData.value}" disabled>
          ${DEFAULT_STATE_SELECT}
        </option>
        ${inputData.options.map(
          ([abbr, state]) =>
            html` <option
              ?selected="${inputData.value === abbr}"
              value="${abbr}">
              ${state}
            </option>`
        )}
      </fs-select>`;
    }
    if (['text', 'number', 'date'].includes(inputData.inputType)) {
      return html`
        <fs-text-field
          type="${inputData.inputType}"
          error-message="${inputData.hasError ? inputData.errorMessage : ''}"
          maxLength="${inputData.maxLength == null ? 256 : inputData.maxLength}"
          ?required="${!inputData.optional}"
          @blur="${this.inputEvent(inputData)}"
          @input="${this.checkPattern(inputData)}"
          .value="${inputData.value}"
          id="${inputData.fieldName.toLowerCase()}"
          >${inputData.label}
        </fs-text-field>
      `;
    }
    return nothing;
  }

  checkPattern(inputData) {
    return ev => {
      const { value } = ev.target;
      if (
        inputData.pattern !== undefined &&
        value &&
        !inputData.pattern.test(value)
      ) {
        ev.target.value = inputData.value;
      } else {
        inputData.value = value;
        if (inputData.validate(value)) {
          inputData.hasError = false;
          this.requestUpdate();
        }
      }
      ev.preventDefault();
    };
  }

  inputEvent(inputData) {
    return ev => {
      const { value } = ev.target;
      this.checkValue(inputData, value);
    };
  }

  /**
   * checks whether or not value is valid
   * @method checkValue
   * @param {InputDef} inputData
   * @param {string | number} value
   */
  checkValue(inputData, value) {
    const isValid = inputData.validate(value);
    this.debugService.log(
      `${inputData.fieldName} is Valid: ${isValid} with ${value} old value ${inputData.value}`
    );
    inputData.hasError = !isValid;
    if (isValid) {
      inputData.value = value;
    }
    const idx = this.additionalInputs.findIndex(
      input => input.fieldName === inputData.fieldName
    );
    if (idx > -1) {
      this.additionalInputs[idx] = inputData;
      publishTagForInputChange(this.config?.documentType, inputData);
      this.requestUpdate();
      this.additionalInfoRepo.setAdditionalInfo(this.additionalInputs);
    }
  }

  /**
   * Handles Form Submit
   * @method onFormSubmit
   * @async
   * @param {SubmitEvent} ev
   * @returns {void}
   */
  async onFormSubmit(ev) {
    ev.preventDefault();
    try {
      this.disableButton = true;
      this.eTitleError = false;
      this.additionalInputs.forEach(input => {
        if (!input.optional) {
          input.hasError = !input.validate(input.value);
        }
      });
      const allValid =
        !this.additionalInputs.length ||
        this.additionalInputs.every(input => !input.hasError);
      if (allValid) {
        const fields = this.additionalInputs.map(input => ({
          fieldType:
            input.fieldName === MICHIGAN_BORROWER_DL_STATE ||
            input.fieldName === DC_BORROWER_DL_STATE
              ? BORROWER_DL_STATE
              : input.fieldName,
          fieldValue: input.value
        }));
        await EsignService.eSignTitle(fields, this.config?.documentType);
        this.sendNextMFE();
      } else {
        this.debugService.error(
          'ERROR: Some fields have errors',
          this.additionalInputs
        );
      }
    } catch (ex) {
      this.eTitleError = true;
      this.debugService.error('Unable to submit signed ETitle', ex);
    } finally {
      this.disableButton = false;
    }
  }

  /**
   * Event Handler for Skip Button Click
   * @method onSkipButtonClick
   * @param {MouseEvent} ev
   * @returns {void}
   */
  onSkipButtonClick(ev) {
    ev.preventDefault();
    const tagMap = {
      [TitleType.PoA]: TagKey.POA__SKIP_FOR_NOW_ON_POA,
      [TitleType.TitleAuth]:
        TagKey.TITLE_AUTHORIZATION_SKIP_FOR_NOW_ON_TITLE_AUTHORIZATION,
      [TitleType.TitleApp]:
        TagKey.TITLE_APPLICATION_SKIP_FOR_NOW_ON_TITLE_APPLICATION,
      [TitleType.SecurityAgreement]:
        TagKey.SECURITY_AGREEMENT_SKIP_FOR_NOW_ON_SECURITY_AGREEMENT
    };
    stratum.publishTag(tagMap[this.config?.documentType]);
    this.debugService.log('Skip Button Clicked');
    this.sendNextMFE();
  }

  sendNextMFE() {
    const ev = new CustomEvent('host-app-event', {
      detail: {
        actionName: 'next-mfe'
      },
      composed: true,
      bubbles: true
    });
    this.dispatchEvent(ev);
  }

  onToggle() {
    stratum.publishTag(
      TagKey.POA_AND_TITLE_AUTHORIZATION_WHY_TRANSFERRING_YOUR_TITLE_MATTERS_ACCORDION_EXPANDED
    );
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('experiments')) {
      this.isPoataUnskippable =
        this.experiments[REFI_ESIGN_CONST.EXPERIMENTS.POATA_REMOVE_SKIP]
          ?.allocation ===
        REFI_ESIGN_CONST.EXPERIMENTS.POATA_REMOVE_SKIP_TEST_VARIANT;
      this.debugService.log(
        `Customer is in POATA unskippable test variant: ${this.isPoataUnskippable}`
      );
      this.debugService.log(
        `poata-remove-skip experiment allocation: ${
          this.experiments[REFI_ESIGN_CONST.EXPERIMENTS.POATA_REMOVE_SKIP]
            ?.allocation
        }`
      );
    }
  }

  render() {
    this.debugService?.log?.(
      `Value of isPoataUnskippable: ${this.isPoataUnskippable}`
    );
    return html` ${this.loading
      ? html` <refi-loading-spinner
          className="_gb-show"></refi-loading-spinner>`
      : html`
          <div class="etitle-container">
            <fs-heading level="1" size="medium" id="poata-heading"
            >Next, review and e-sign your title transfer
            </fs-heading
            >
            <fs-body-text size="normal" variant="primary">
              ${getEtitleHeader(this.config?.documentType)}
            </fs-body-text>
            <esign-accordion icon-position="trailing" size="medium">
              <esign-accordion-item
                  @toggle="${this.onToggle}"
                  ?expanded="false"
                  ?disabled="false"
              >
                <h2 slot="fs-accordion-item-title">Why transferring your title
                  matters</h2>
                <p slot="fs-accordion-item-content">
                  Replacing your old lender on your title with Capital One will
                  keep your title information up to date. If your title
                  information is outdated, it can lead to issues with:
                  <ul slot="fs-accordion-item-content">
                    <li>Registering or selling your vehicle</li>
                    <li>Delayed or refused insurance payments if your vehicle is damaged or totaled</li>
                    <li>Obtaining the title after paying off your loan</li>
                  </ul>
                </p>
              </esign-accordion-item>
              <fs-divider emphasis="low"></fs-divider>
            </esign-accordion>
            <form id="etitle-form">
              ${
                this.additionalInputs?.length > 0
                  ? html` <fs-body-text size="normal" variant="primary">
                        We’ll just need you to fill out a bit more info, then
                        you can review and e-sign.
                      </fs-body-text>
                      <div class="additional-info">
                        <div class="additional-info borrower-info">
                          ${this.generateBorrowerFormFields()}
                        </div>
                        ${this.additionalInputs.some(
                          input => input.isCoBorrowerInput
                        )
                          ? html` <div class="additional-info coborrower-info">
                              <fs-heading level="2" size="small"
                                >Co-borrower license
                              </fs-heading>
                              ${this.generateCoBorrowerFormFields()}
                            </div>`
                          : nothing}
                      </div>`
                  : nothing
              }
              <div class="actions">
                <etitle-download
                    etitle-type="${
                      this.config?.documentType
                    }"></etitle-download>
                <refi-etitle-modal
                    .etitleType="${this.config?.documentType}"
                    .showCoBorrowerVerbiage="${
                      this.config?.hasCoBorrower &&
                      this.config?.documentType !== TitleType.PoA
                    }"
                    .eTitleError="${this.eTitleError}"
                    additional-inputs="${JSON.stringify(this.additionalInputs)}"
                    @agree-click="${this.onFormSubmit}"
                    ?loading="${this.disableButton}">
                </refi-etitle-modal>
                ${
                  !this.isPoataUnskippable
                    ? html`
                        <fs-button
                          type="button"
                          id="skip-btn"
                          @click="${this.onSkipButtonClick}"
                          inverted
                          compact>
                          <span id="skip-span">Skip for now</span>
                        </fs-button>
                      `
                    : nothing
                }
              </div>
            </form>
          </div>`}`;
  }
}

customElements.define('refi-etitle', ETitle);
